import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Settings, Users, Clock, Calendar, ArrowLeft, Globe,
  ChevronRight, CheckCircle, AlertCircle, Shield, Zap,
  BarChart2, TrendingUp, Target, Building2,
  Phone, MessageSquare, Star, RefreshCw, Award,
  Layers, GitBranch, FileText, GraduationCap
} from 'lucide-react';
import { getAllRoles, getCoverageModel } from '../lib/rolesRegistry';
import { getAllStages } from '../lib/leadPipelineRegistry';
import { getEscalationRules, getSLACounts } from '../lib/slaCatalogueRegistry';

// ─── Types ────────────────────────────────────────────────────────────────────
interface CadenceItem {
  time: string;
  timeAr: string;
  activity: string;
  activityAr: string;
  roles: string[];
  rolesAr: string[];
  channel: 'meeting' | 'crm' | 'call' | 'report' | 'review';
}

interface GovernanceForum {
  id: string;
  name: string;
  nameAr: string;
  frequency: string;
  frequencyAr: string;
  owner: string;
  ownerAr: string;
  attendees: string[];
  attendeesAr: string[];
  agenda: string[];
  agendaAr: string[];
  outputs: string[];
  outputsAr: string[];
  color: string;
}

// ─── Daily Cadence Data ────────────────────────────────────────────────────────
const DAILY_CADENCE: CadenceItem[] = [
  {
    time: '08:00', timeAr: '8:00 ص',
    activity: 'Morning Shift Start – BDC & Sales Advisor Briefing',
    activityAr: 'بدء الوردية الصباحية – إحاطة مركز تطوير الأعمال ومستشاري المبيعات',
    roles: ['BDC Supervisor', 'Sales Manager'],
    rolesAr: ['مشرف مركز تطوير الأعمال', 'مدير المبيعات'],
    channel: 'meeting'
  },
  {
    time: '08:30', timeAr: '8:30 ص',
    activity: 'SLA queue review – all overnight and after-hours leads actioned',
    activityAr: 'مراجعة قائمة الانتظار SLA – معالجة جميع العملاء المحتملين الليليين وما بعد ساعات العمل',
    roles: ['BDC Agent', 'BDC Supervisor'],
    rolesAr: ['وكيل مركز تطوير الأعمال', 'مشرف مركز تطوير الأعمال'],
    channel: 'crm'
  },
  {
    time: '09:00', timeAr: '9:00 ص',
    activity: 'Active outbound contact cadence begins (Stage 1 & 2 leads)',
    activityAr: 'تبدأ دورة الاتصال الصادر النشط (عملاء المرحلة 1 و 2)',
    roles: ['BDC Agent'],
    rolesAr: ['وكيل مركز تطوير الأعمال'],
    channel: 'call'
  },
  {
    time: '10:00', timeAr: '10:00 ص',
    activity: 'Peak hours begin – Captain and Finance Officer on station',
    activityAr: 'تبدأ ساعات الذروة – الكابتن وموظف التمويل في مواقعهم',
    roles: ['Captain', 'Finance Officer'],
    rolesAr: ['الكابتن', 'موظف التمويل'],
    channel: 'meeting'
  },
  {
    time: '12:00', timeAr: '12:00 م',
    activity: 'Mid-day WhatsApp follow-up wave (Stage 2 cadence Day 2)',
    activityAr: 'موجة متابعة واتساب منتصف اليوم (يوم 2 من دورة المرحلة 2)',
    roles: ['BDC Agent'],
    rolesAr: ['وكيل مركز تطوير الأعمال'],
    channel: 'call'
  },
  {
    time: '13:00', timeAr: '1:00 م',
    activity: 'Supervisor CRM data quality spot-check (random 10% audit)',
    activityAr: 'مراجعة عشوائية لجودة بيانات CRM من قِبل المشرف (تدقيق عشوائي 10%)',
    roles: ['BDC Supervisor', 'Quality Analyst'],
    rolesAr: ['مشرف مركز تطوير الأعمال', 'محلل الجودة'],
    channel: 'crm'
  },
  {
    time: '14:00', timeAr: '2:00 م',
    activity: 'Appointment preparation – advisors review tomorrow\'s confirmed visits',
    activityAr: 'تحضير المواعيد – يراجع المستشارون الزيارات المؤكدة لغد',
    roles: ['Sales Advisor', 'Delivery Coordinator'],
    rolesAr: ['مستشار المبيعات', 'منسق التسليم'],
    channel: 'crm'
  },
  {
    time: '16:00', timeAr: '4:00 م',
    activity: 'Afternoon contact wave – last call attempts before close (Stage 2 Day 1)',
    activityAr: 'موجة الاتصال المسائية – آخر محاولات الاتصال قبل الإغلاق (يوم 1 من المرحلة 2)',
    roles: ['BDC Agent'],
    rolesAr: ['وكيل مركز تطوير الأعمال'],
    channel: 'call'
  },
  {
    time: '16:30', timeAr: '4:30 م',
    activity: 'End-of-day CRM update – all leads must have next action set',
    activityAr: 'تحديث CRM نهاية اليوم – يجب تحديد الإجراء التالي لجميع العملاء',
    roles: ['BDC Agent', 'Sales Advisor'],
    rolesAr: ['وكيل مركز تطوير الأعمال', 'مستشار المبيعات'],
    channel: 'crm'
  },
  {
    time: '17:00', timeAr: '5:00 م',
    activity: 'After-hours auto-reply activation – WhatsApp queue to next shift',
    activityAr: 'تفعيل الرد التلقائي بعد ساعات العمل – قائمة انتظار واتساب للوردية التالية',
    roles: ['CRM Administrator'],
    rolesAr: ['مدير CRM'],
    channel: 'crm'
  }
];

// ─── Weekly Cadence ────────────────────────────────────────────────────────────
const WEEKLY_CADENCE = [
  {
    day: 'Sunday', dayAr: 'الأحد',
    activities: [
      { time: '09:00', en: 'Weekly team briefing – targets, pipeline review, priorities', ar: 'إحاطة الفريق الأسبوعية – الأهداف ومراجعة المسار والأولويات', roles: ['BDC Supervisor', 'Sales Manager'] },
      { time: '10:00', en: 'CRM data clean-up: duplicates, stale leads, incomplete fields', ar: 'تنظيف بيانات CRM: التكرارات والعملاء الراكدين والحقول غير المكتملة', roles: ['CRM Administrator', 'Quality Analyst'] }
    ]
  },
  {
    day: 'Tuesday', dayAr: 'الثلاثاء',
    activities: [
      { time: '11:00', en: 'Mid-week pipeline health check – SLA compliance review', ar: 'فحص صحة المسار منتصف الأسبوع – مراجعة الامتثال لـ SLA', roles: ['BDC Supervisor', 'Sales Manager'] },
      { time: '14:00', en: 'Trade-in valuation review – pending assessments and approvals', ar: 'مراجعة تقييمات المقايضة – التقييمات والموافقات المعلقة', roles: ['Trade-in Evaluator', 'Sales Manager'] }
    ]
  },
  {
    day: 'Thursday', dayAr: 'الخميس',
    activities: [
      { time: '09:00', en: 'Weekly KPI report generation and distribution', ar: 'إنشاء وتوزيع تقرير مؤشرات الأداء الأسبوعي', roles: ['Quality Analyst', 'BDC Supervisor'] },
      { time: '15:00', en: 'Finance pipeline review – deals in Stage 8 (Negotiation)', ar: 'مراجعة مسار التمويل – الصفقات في المرحلة 8 (التفاوض)', roles: ['Finance Officer', 'Sales Manager'] }
    ]
  },
  {
    day: 'Friday', dayAr: 'الجمعة',
    activities: [
      { time: '10:00', en: 'Delivery coordination – confirm next week\'s scheduled deliveries', ar: 'تنسيق التسليم – تأكيد التسليمات المجدولة للأسبوع القادم', roles: ['Delivery Coordinator', 'Sales Advisor'] }
    ]
  }
];

// ─── Governance Forums ─────────────────────────────────────────────────────────
const GOVERNANCE_FORUMS: GovernanceForum[] = [
  {
    id: 'weekly-ops',
    name: 'Weekly Operations Review',
    nameAr: 'مراجعة العمليات الأسبوعية',
    frequency: 'Weekly – Sunday 09:00',
    frequencyAr: 'أسبوعياً – الأحد 9:00 ص',
    owner: 'Sales Manager',
    ownerAr: 'مدير المبيعات',
    attendees: ['Sales Manager', 'BDC Supervisor', 'Sales Advisors', 'Finance Officer'],
    attendeesAr: ['مدير المبيعات', 'مشرف مركز تطوير الأعمال', 'مستشارو المبيعات', 'موظف التمويل'],
    agenda: ['Pipeline stage distribution review', 'SLA compliance report', 'Conversion rate vs target', 'Upcoming appointments', 'Team capacity and coverage'],
    agendaAr: ['مراجعة توزيع مراحل المسار', 'تقرير الامتثال لـ SLA', 'معدل التحويل مقابل الهدف', 'المواعيد القادمة', 'قدرة الفريق والتغطية'],
    outputs: ['Action items log', 'Adjusted daily priorities', 'Escalation resolutions'],
    outputsAr: ['سجل بنود العمل', 'الأولويات اليومية المعدلة', 'قرارات التصعيد'],
    color: 'border-blue-500'
  },
  {
    id: 'monthly-performance',
    name: 'Monthly Performance Review',
    nameAr: 'مراجعة الأداء الشهرية',
    frequency: 'Monthly – 1st Sunday',
    frequencyAr: 'شهرياً – أول أحد',
    owner: 'Branch Manager',
    ownerAr: 'مدير الفرع',
    attendees: ['Branch Manager', 'Sales Manager', 'BDC Supervisor', 'Quality Analyst'],
    attendeesAr: ['مدير الفرع', 'مدير المبيعات', 'مشرف مركز تطوير الأعمال', 'محلل الجودة'],
    agenda: ['Full KPI scorecard review', 'Role performance against targets', 'Training completion status', 'Process improvement initiatives', 'PDPL compliance audit', 'Next month targets'],
    agendaAr: ['مراجعة كاملة لبطاقة الأداء', 'أداء الأدوار مقابل الأهداف', 'حالة اكتمال التدريب', 'مبادرات تحسين العملية', 'تدقيق الامتثال لـ PDPL', 'أهداف الشهر القادم'],
    outputs: ['Scorecard sign-off', 'Performance improvement plans', 'Training assignments', 'Process change requests'],
    outputsAr: ['اعتماد بطاقة الأداء', 'خطط تحسين الأداء', 'تعيينات التدريب', 'طلبات تغيير العملية'],
    color: 'border-emerald-500'
  },
  {
    id: 'daily-standup',
    name: 'Daily BDC Stand-up',
    nameAr: 'الاجتماع اليومي لمركز تطوير الأعمال',
    frequency: 'Daily – 08:00 (15 min max)',
    frequencyAr: 'يومياً – 8:00 ص (15 دقيقة كحد أقصى)',
    owner: 'BDC Supervisor',
    ownerAr: 'مشرف مركز تطوير الأعمال',
    attendees: ['BDC Supervisor', 'All BDC Agents'],
    attendeesAr: ['مشرف مركز تطوير الأعمال', 'جميع وكلاء مركز تطوير الأعمال'],
    agenda: ['Yesterday\'s contact rate and SLA compliance', 'Today\'s priority leads', 'Any escalations or blockers', 'Capacity and coverage for the day'],
    agendaAr: ['معدل الاتصال والامتثال لـ SLA أمس', 'العملاء المحتملون ذوو الأولوية اليوم', 'أي تصعيدات أو عوائق', 'القدرة والتغطية لليوم'],
    outputs: ['Daily priority list', 'Escalation assignments', 'Coverage confirmation'],
    outputsAr: ['قائمة الأولويات اليومية', 'تعيينات التصعيد', 'تأكيد التغطية'],
    color: 'border-amber-500'
  },
  {
    id: 'quality-review',
    name: 'Quality & Compliance Review',
    nameAr: 'مراجعة الجودة والامتثال',
    frequency: 'Bi-weekly – Thursday',
    frequencyAr: 'نصف شهري – الخميس',
    owner: 'Quality Analyst',
    ownerAr: 'محلل الجودة',
    attendees: ['Quality Analyst', 'BDC Supervisor', 'CRM Administrator'],
    attendeesAr: ['محلل الجودة', 'مشرف مركز تطوير الأعمال', 'مدير CRM'],
    agenda: ['CRM data quality audit results', 'Call monitoring scores', 'PDPL consent compliance', 'Exception code frequency analysis', 'Process adherence rate'],
    agendaAr: ['نتائج تدقيق جودة بيانات CRM', 'درجات مراقبة المكالمات', 'امتثال موافقة PDPL', 'تحليل تكرار رمز الاستثناء', 'معدل الالتزام بالعملية'],
    outputs: ['Quality report', 'Corrective action items', 'Training recommendations'],
    outputsAr: ['تقرير الجودة', 'بنود الإجراء التصحيحي', 'توصيات التدريب'],
    color: 'border-purple-500'
  }
];

// ─── Org Structure ─────────────────────────────────────────────────────────────
const ORG_GROUPS = [
  {
    id: 'bdc',
    label: 'Business Development Center',
    labelAr: 'مركز تطوير الأعمال',
    roles: ['BDC Supervisor', 'BDC Agent'],
    rolesAr: ['مشرف مركز تطوير الأعمال', 'وكيل مركز تطوير الأعمال'],
    headcount: { min: 3, recommended: 5 },
    color: 'bg-blue-600',
    lightColor: 'bg-blue-50 border-blue-200',
    textColor: 'text-blue-800',
    focus: 'Lead intake, contact cadence, qualification, appointment setting',
    focusAr: 'استقبال العملاء المحتملين، دورة الاتصال، التأهيل، تحديد المواعيد'
  },
  {
    id: 'sales',
    label: 'Sales Team',
    labelAr: 'فريق المبيعات',
    roles: ['Sales Manager', 'Sales Advisor', 'Captain (Test Drive)'],
    rolesAr: ['مدير المبيعات', 'مستشار المبيعات', 'الكابتن (قيادة تجريبية)'],
    headcount: { min: 6, recommended: 10 },
    color: 'bg-emerald-600',
    lightColor: 'bg-emerald-50 border-emerald-200',
    textColor: 'text-emerald-800',
    focus: 'Showroom engagement, test drives, negotiation, closing',
    focusAr: 'تفاعل المعرض، القيادة التجريبية، التفاوض، الإغلاق'
  },
  {
    id: 'finance',
    label: 'Finance & Trade-in',
    labelAr: 'التمويل والمقايضة',
    roles: ['Finance Officer', 'Trade-in Evaluator'],
    rolesAr: ['موظف التمويل', 'مقيم المقايضة'],
    headcount: { min: 2, recommended: 3 },
    color: 'bg-purple-600',
    lightColor: 'bg-purple-50 border-purple-200',
    textColor: 'text-purple-800',
    focus: 'Finance structuring, trade-in valuation, document processing',
    focusAr: 'هيكلة التمويل، تقييم المقايضة، معالجة المستندات'
  },
  {
    id: 'operations',
    label: 'Operations & Quality',
    labelAr: 'العمليات والجودة',
    roles: ['Delivery Coordinator', 'CRM Administrator', 'Quality Analyst'],
    rolesAr: ['منسق التسليم', 'مدير CRM', 'محلل الجودة'],
    headcount: { min: 3, recommended: 4 },
    color: 'bg-orange-600',
    lightColor: 'bg-orange-50 border-orange-200',
    textColor: 'text-orange-800',
    focus: 'Vehicle delivery, CRM system management, quality audits',
    focusAr: 'تسليم المركبات، إدارة نظام CRM، تدقيق الجودة'
  }
];

const channelIcons: Record<string, React.ReactNode> = {
  meeting: <Users className="w-4 h-4 text-blue-600" />,
  crm: <Settings className="w-4 h-4 text-purple-600" />,
  call: <Phone className="w-4 h-4 text-green-600" />,
  report: <BarChart2 className="w-4 h-4 text-orange-600" />,
  review: <CheckCircle className="w-4 h-4 text-teal-600" />
};

const channelColors: Record<string, string> = {
  meeting: 'bg-blue-50 border-blue-200',
  crm: 'bg-purple-50 border-purple-200',
  call: 'bg-green-50 border-green-200',
  report: 'bg-orange-50 border-orange-200',
  review: 'bg-teal-50 border-teal-200'
};

// ─── Main Component ───────────────────────────────────────────────────────────
const OperatingModel = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'org' | 'cadences' | 'governance' | 'coverage'>('overview');
  const [lang, setLang] = useState<'en' | 'ar'>('en');
  const [expandedForum, setExpandedForum] = useState<string | null>(null);

  const roles = getAllRoles();
  const stages = getAllStages();
  const escalationRules = getEscalationRules();
  const slaCounts = getSLACounts();
  const coverageModel = getCoverageModel();

  return (
    <div className={`min-h-screen bg-gray-50 ${lang === 'ar' ? 'rtl font-arabic' : 'ltr'}`}>
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-700 via-slate-600 to-gray-700 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-4">
            <Link to="/" className="flex items-center gap-2 text-slate-300 hover:text-white text-sm transition-colors">
              <ArrowLeft className="w-4 h-4" />
              {lang === 'ar' ? 'الرئيسية' : 'Home'}
            </Link>
            <button
              onClick={() => setLang(l => l === 'en' ? 'ar' : 'en')}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg text-sm transition-colors"
            >
              <Globe className="w-4 h-4" />
              {lang === 'en' ? 'العربية' : 'English'}
            </button>
          </div>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center">
              <Building2 className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-3xl font-bold">
                {lang === 'ar' ? 'نموذج التشغيل' : 'Operating Model'}
              </h1>
              <p className="text-slate-300 mt-1">
                {lang === 'ar'
                  ? 'الهيكل التنظيمي · الجداول الزمنية · منتديات الحوكمة · نموذج التغطية'
                  : 'Org structure · Daily & weekly cadences · Governance forums · Coverage model'}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: lang === 'ar' ? 'فريق' : 'Team Groups', value: ORG_GROUPS.length, icon: <Users className="w-5 h-5" /> },
              { label: lang === 'ar' ? 'دور في الفريق' : 'Roles', value: roles.length, icon: <Award className="w-5 h-5" /> },
              { label: lang === 'ar' ? 'منتدى حوكمة' : 'Governance Forums', value: GOVERNANCE_FORUMS.length, icon: <Calendar className="w-5 h-5" /> },
              { label: lang === 'ar' ? 'نشاط يومي' : 'Daily Activities', value: DAILY_CADENCE.length, icon: <Clock className="w-5 h-5" /> }
            ].map((stat, i) => (
              <div key={i} className="bg-white/15 backdrop-blur rounded-xl p-4 flex items-center gap-3">
                <div className="text-slate-300">{stat.icon}</div>
                <div>
                  <div className="text-2xl font-bold">{stat.value}</div>
                  <div className="text-slate-300 text-xs">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto">
            {[
              { id: 'overview', label: lang === 'ar' ? 'نظرة عامة' : 'Overview', icon: <Layers className="w-4 h-4" /> },
              { id: 'org', label: lang === 'ar' ? 'الهيكل التنظيمي' : 'Org Structure', icon: <Building2 className="w-4 h-4" /> },
              { id: 'cadences', label: lang === 'ar' ? 'الجداول الزمنية' : 'Daily & Weekly', icon: <Clock className="w-4 h-4" /> },
              { id: 'governance', label: lang === 'ar' ? 'الحوكمة' : 'Governance Forums', icon: <Calendar className="w-4 h-4" /> },
              { id: 'coverage', label: lang === 'ar' ? 'نموذج التغطية' : 'Coverage Model', icon: <Shield className="w-4 h-4" /> }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as typeof activeTab)}
                className={`flex items-center gap-2 px-5 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-slate-600 text-slate-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Mission Statement */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white rounded-2xl p-6">
              <h2 className="text-xl font-bold mb-2">
                {lang === 'ar' ? 'مبدأ نموذج التشغيل' : 'Operating Model Principle'}
              </h2>
              <p className="text-slate-200 leading-relaxed">
                {lang === 'ar'
                  ? 'يعمل المجدوعي للسيارات وفق نموذج متكامل حيث يمتلك كل فريق دوراً محدداً في رحلة العميل المحتمل من الاستقبال حتى التسليم. تضمن الجداول الزمنية اليومية وهياكل الحوكمة الأسبوعية الاتساق والجودة والامتثال عبر جميع الفروع والعلامات التجارية.'
                  : 'Almajdouie Motors operates on an integrated model where each team owns a defined role in the lead journey from intake to delivery. Daily cadences and weekly governance structures ensure consistency, quality, and compliance across all branches and brands.'}
              </p>
            </div>

            {/* Team Overview Cards */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {lang === 'ar' ? 'مجموعات الفريق' : 'Team Groups'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {ORG_GROUPS.map(group => (
                  <div key={group.id} className={`bg-white border rounded-xl p-5 ${group.lightColor} border-l-4`}
                    style={{ borderLeftColor: group.color.replace('bg-', '').includes('-600') ? undefined : undefined }}>
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium mb-3 ${group.lightColor} ${group.textColor}`}>
                      <span className={`w-2 h-2 rounded-full ${group.color}`} />
                      {lang === 'ar' ? group.labelAr : group.label}
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-2xl font-bold text-gray-900">{group.headcount.recommended}</span>
                      <span className="text-xs text-gray-500">{lang === 'ar' ? 'مُوصى به' : 'recommended'}</span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      {lang === 'ar' ? group.focusAr : group.focus}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {(lang === 'ar' ? group.rolesAr : group.roles).map((role, i) => (
                        <span key={i} className={`text-xs px-2 py-0.5 rounded-full ${group.lightColor} ${group.textColor} border`}>
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pipeline Integration */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {lang === 'ar' ? 'تكامل مسار المبيعات' : 'Pipeline Integration'}
              </h2>
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
                  <GitBranch className="w-4 h-4" />
                  {lang === 'ar' ? `${stages.length} مرحلة في مسار المبيعات – كل مرحلة لها صاحب وSLA محدد` : `${stages.length} pipeline stages – each with defined owner and SLA`}
                </div>
                <div className="overflow-x-auto">
                  <div className="flex gap-2 min-w-max">
                    {stages.filter(s => s.meta.type === 'stage').map((stage) => (
                      <div key={stage.meta.id} className="flex flex-col items-center gap-1">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm"
                          style={{ backgroundColor: stage.meta.color }}
                        >
                          {stage.meta.order}
                        </div>
                        <div className="text-xs text-gray-600 text-center max-w-16 leading-tight">
                          {stage.meta.title.split(' ').slice(0, 2).join('\n')}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{slaCounts.channelSLAs}</div>
                    <div className="text-xs text-gray-500">{lang === 'ar' ? 'اتفاقيات SLA للقنوات' : 'Channel SLAs'}</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-600">{slaCounts.stageSLAs}</div>
                    <div className="text-xs text-gray-500">{lang === 'ar' ? 'اتفاقيات SLA للمراحل' : 'Stage SLAs'}</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-orange-600">{escalationRules.length}</div>
                    <div className="text-xs text-gray-500">{lang === 'ar' ? 'قواعد التصعيد' : 'Escalation Rules'}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Governance Links */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                {lang === 'ar' ? 'روابط الحوكمة' : 'Governance Links'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { label: lang === 'ar' ? 'السياسات' : 'Policies', path: '/policies', icon: <Shield className="w-4 h-4 text-red-500" /> },
                  { label: lang === 'ar' ? 'إدارة الأداء' : 'Performance', path: '/performance', icon: <TrendingUp className="w-4 h-4 text-indigo-500" /> },
                  { label: lang === 'ar' ? 'كتالوج SLA' : 'SLA Catalogue', path: '/sla-catalogue', icon: <Clock className="w-4 h-4 text-green-500" /> },
                  { label: lang === 'ar' ? 'الأدوار والمسؤوليات' : 'Roles & Responsibilities', path: '/roles', icon: <Users className="w-4 h-4 text-blue-500" /> },
                  { label: lang === 'ar' ? 'إجراءات التشغيل الموحدة' : 'SOPs', path: '/sops', icon: <FileText className="w-4 h-4 text-orange-500" /> },
                  { label: lang === 'ar' ? 'التدريب' : 'Training', path: '/training', icon: <GraduationCap className="w-4 h-4 text-purple-500" /> }
                ].map((link, i) => (
                  <Link key={i} to={link.path}
                    className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 hover:border-slate-400 transition-colors">
                    {link.icon}
                    {link.label}
                    <ChevronRight className="w-3 h-3 ml-auto text-slate-400" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── ORG STRUCTURE TAB ── */}
        {activeTab === 'org' && (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <div className="text-sm text-blue-800">
                {lang === 'ar'
                  ? 'هذا الهيكل يمثل نموذج التشغيل القياسي لكل فرع. تعيين الدور الفعلي يتم في نظام CRM من قِبل مدير CRM.'
                  : 'This structure represents the standard operating model per branch. Actual role assignment is managed in the CRM system by the CRM Administrator.'}
              </div>
            </div>

            {/* Org Chart Visual */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                {lang === 'ar' ? 'الهيكل التنظيمي – مستوى الفرع' : 'Organizational Structure – Branch Level'}
              </h2>

              {/* Branch Manager */}
              <div className="flex justify-center mb-6">
                <div className="bg-slate-700 text-white rounded-xl px-6 py-3 text-center shadow-lg">
                  <div className="font-bold text-lg">Branch Manager</div>
                  <div className="text-slate-300 text-xs">مدير الفرع</div>
                </div>
              </div>

              {/* Connector line */}
              <div className="flex justify-center mb-4">
                <div className="w-0.5 h-8 bg-gray-300" />
              </div>

              {/* 4 Groups */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {ORG_GROUPS.map(group => (
                  <div key={group.id} className={`rounded-xl border-2 p-4 ${group.lightColor}`}>
                    <div className={`text-center font-bold text-sm mb-3 ${group.textColor}`}>
                      {lang === 'ar' ? group.labelAr : group.label}
                    </div>
                    <div className="space-y-2">
                      {group.roles.map((role, i) => {
                        const roleAr = group.rolesAr[i];
                        const coverage = coverageModel?.rolesPerBranch?.find(r =>
                          r.roleId.includes(role.toLowerCase().replace(/ /g, '_').replace(/[()]/g, ''))
                        );
                        return (
                          <div key={i} className="bg-white rounded-lg p-2.5 border border-gray-200 shadow-sm">
                            <div className="font-medium text-gray-800 text-xs">{lang === 'ar' ? roleAr : role}</div>
                            {coverage && (
                              <div className="flex items-center gap-1 mt-1">
                                <span className="text-xs text-gray-400">Min: {coverage.minimumCount}</span>
                                <span className="text-xs text-gray-300">·</span>
                                <span className="text-xs text-gray-400">Rec: {coverage.recommendedCount}</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{lang === 'ar' ? 'أدنى حد' : 'Min'}</span>
                        <span className="font-bold text-gray-700">{group.headcount.min}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>{lang === 'ar' ? 'مُوصى به' : 'Recommended'}</span>
                        <span className="font-bold text-gray-700">{group.headcount.recommended}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Role Detail Cards */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {lang === 'ar' ? 'ملخص الأدوار' : 'Role Summary'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {roles.map(role => {
                  const group = ORG_GROUPS.find(g => g.id === role.group);
                  return (
                    <Link
                      key={role.id}
                      to={`/roles/${role.id}`}
                      className={`bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-shadow flex items-start gap-3 ${group ? 'border-l-4' : ''}`}
                      style={group ? { borderLeftColor: group.color.includes('blue') ? '#2563eb' : group.color.includes('emerald') ? '#059669' : group.color.includes('purple') ? '#7c3aed' : '#ea580c' } : {}}
                    >
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0 ${group?.color || 'bg-gray-500'}`}>
                        {role.title.split(' ').map((w: string) => w[0]).slice(0, 2).join('')}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 text-sm">{lang === 'ar' ? role.titleAr : role.title}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{lang === 'ar' ? role.departmentAr : role.department}</div>
                        {role.trainingRequired?.length > 0 && (
                          <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                            <GraduationCap className="w-3 h-3" />
                            {role.trainingRequired.length} {lang === 'ar' ? 'وحدة تدريبية' : 'training modules'}
                          </div>
                        )}
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 mt-1" />
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ── CADENCES TAB ── */}
        {activeTab === 'cadences' && (
          <div className="space-y-8">
            {/* Daily Cadence */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-600" />
                {lang === 'ar' ? 'الجدول اليومي' : 'Daily Cadence'}
                <span className="text-sm font-normal text-gray-500 ml-2">
                  {lang === 'ar' ? '8:00 ص – 5:00 م' : '08:00 – 17:00 (Operating Hours)'}
                </span>
              </h2>

              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-16 top-0 bottom-0 w-0.5 bg-gray-200 hidden md:block" />

                <div className="space-y-3">
                  {DAILY_CADENCE.map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      {/* Time */}
                      <div className="w-12 text-right shrink-0">
                        <span className="text-sm font-mono font-medium text-gray-700">
                          {lang === 'ar' ? item.timeAr : item.time}
                        </span>
                      </div>

                      {/* Dot */}
                      <div className="relative hidden md:flex items-center justify-center w-7 shrink-0 mt-1">
                        <div className={`w-7 h-7 rounded-full border-2 border-white shadow flex items-center justify-center ${channelColors[item.channel]}`}>
                          {channelIcons[item.channel]}
                        </div>
                      </div>

                      {/* Content */}
                      <div className={`flex-1 border rounded-xl p-3 ${channelColors[item.channel]}`}>
                        <p className="text-sm font-medium text-gray-800">
                          {lang === 'ar' ? item.activityAr : item.activity}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {(lang === 'ar' ? item.rolesAr : item.roles).map((role, ri) => (
                            <span key={ri} className="text-xs bg-white/70 text-gray-600 px-2 py-0.5 rounded-full border border-gray-200">
                              {role}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Weekly Cadence */}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-600" />
                {lang === 'ar' ? 'الجدول الأسبوعي' : 'Weekly Cadence'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {WEEKLY_CADENCE.map((day, i) => (
                  <div key={i} className="bg-white border border-gray-200 rounded-xl p-5">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      {lang === 'ar' ? day.dayAr : day.day}
                    </h3>
                    <div className="space-y-3">
                      {day.activities.map((act, ai) => (
                        <div key={ai} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                          <div className="flex items-start gap-2">
                            <span className="text-xs font-mono bg-white border border-gray-200 px-2 py-0.5 rounded text-gray-600 shrink-0">
                              {act.time}
                            </span>
                            <p className="text-sm text-gray-700">{lang === 'ar' ? act.ar : act.en}</p>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {act.roles.map((r, ri) => (
                              <span key={ri} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                                {r}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Cadence Summary */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                {lang === 'ar' ? 'دورة اتصال العميل المحتمل – ملخص 8 محاولات (المرحلة 2)' : 'Lead Contact Cadence – 8-Attempt Summary (Stage 2)'}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { day: 'Day 1', dayAr: 'اليوم 1', attempts: ['08:30 Call', '10:30 WhatsApp', '12:30 Call', '16:30 Call'], attemptsAr: ['8:30 اتصال', '10:30 واتساب', '12:30 اتصال', '16:30 اتصال'] },
                  { day: 'Day 2', dayAr: 'اليوم 2', attempts: ['08:30 Call', '12:00 WhatsApp', '16:00 Call'], attemptsAr: ['8:30 اتصال', '12:00 واتساب', '16:00 اتصال'] },
                  { day: 'Day 3', dayAr: 'اليوم 3', attempts: ['10:00 SMS + WhatsApp'], attemptsAr: ['10:00 رسالة نصية + واتساب'] },
                  { day: 'Outcome', dayAr: 'النتيجة', attempts: ['Stage 3 (Connected)', 'Unreachable', 'Nurture'], attemptsAr: ['المرحلة 3 (تم التواصل)', 'لا يمكن الوصول', 'برنامج التربية'] }
                ].map((day, i) => (
                  <div key={i} className="bg-white border border-green-200 rounded-lg p-3">
                    <div className="font-medium text-green-800 text-sm mb-2">{lang === 'ar' ? day.dayAr : day.day}</div>
                    <div className="space-y-1">
                      {(lang === 'ar' ? day.attemptsAr : day.attempts).map((a, ai) => (
                        <div key={ai} className="text-xs text-gray-600 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                          {a}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── GOVERNANCE FORUMS TAB ── */}
        {activeTab === 'governance' && (
          <div className="space-y-5">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700">
              {lang === 'ar'
                ? 'منتديات الحوكمة هي الاجتماعات الرسمية التي تضمن المساءلة والتحسين المستمر وحل المشكلات عبر جميع الأدوار والمراحل.'
                : 'Governance forums are the formal meeting structures that ensure accountability, continuous improvement, and issue resolution across all roles and stages.'}
            </div>

            {GOVERNANCE_FORUMS.map(forum => {
              const isExpanded = expandedForum === forum.id;
              return (
                <div key={forum.id} className={`bg-white border border-gray-200 rounded-xl overflow-hidden border-l-4 ${forum.color}`}>
                  <div
                    className="p-5 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => setExpandedForum(isExpanded ? null : forum.id)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-gray-900 text-lg">
                          {lang === 'ar' ? forum.nameAr : forum.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 mt-1">
                          <span className="flex items-center gap-1 text-sm text-gray-500">
                            <RefreshCw className="w-3 h-3" />
                            {lang === 'ar' ? forum.frequencyAr : forum.frequency}
                          </span>
                          <span className="flex items-center gap-1 text-sm text-gray-500">
                            <Star className="w-3 h-3" />
                            {lang === 'ar' ? forum.ownerAr : forum.owner}
                          </span>
                          <span className="flex items-center gap-1 text-sm text-gray-500">
                            <Users className="w-3 h-3" />
                            {forum.attendees.length} {lang === 'ar' ? 'حضور' : 'attendees'}
                          </span>
                        </div>
                      </div>
                      <button className="text-gray-400">
                        {isExpanded ? <ChevronRight className="w-5 h-5 rotate-90 transition-transform" /> : <ChevronRight className="w-5 h-5 transition-transform" />}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 p-5 grid grid-cols-1 md:grid-cols-3 gap-5 bg-gray-50">
                      {/* Attendees */}
                      <div>
                        <h4 className="font-medium text-gray-700 text-sm mb-2 flex items-center gap-1">
                          <Users className="w-4 h-4 text-slate-500" />
                          {lang === 'ar' ? 'الحضور' : 'Attendees'}
                        </h4>
                        <div className="space-y-1">
                          {(lang === 'ar' ? forum.attendeesAr : forum.attendees).map((a, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                              <span className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                              {a}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Agenda */}
                      <div>
                        <h4 className="font-medium text-gray-700 text-sm mb-2 flex items-center gap-1">
                          <FileText className="w-4 h-4 text-slate-500" />
                          {lang === 'ar' ? 'جدول الأعمال' : 'Agenda'}
                        </h4>
                        <div className="space-y-1">
                          {(lang === 'ar' ? forum.agendaAr : forum.agenda).map((item, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                              <span className="text-slate-400 font-mono text-xs mt-0.5">{i + 1}.</span>
                              {item}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Outputs */}
                      <div>
                        <h4 className="font-medium text-gray-700 text-sm mb-2 flex items-center gap-1">
                          <Target className="w-4 h-4 text-slate-500" />
                          {lang === 'ar' ? 'المخرجات' : 'Outputs'}
                        </h4>
                        <div className="space-y-1">
                          {(lang === 'ar' ? forum.outputsAr : forum.outputs).map((o, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm text-gray-700">
                              <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                              {o}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── COVERAGE MODEL TAB ── */}
        {activeTab === 'coverage' && (
          <div className="space-y-6">
            {/* Minimum Staffing Alert */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <div className="font-medium text-amber-900 text-sm">
                  {lang === 'ar' ? 'متطلبات الحد الأدنى من الطاقم' : 'Minimum Staffing Requirements'}
                </div>
                <div className="text-sm text-amber-800 mt-1">
                  {lang === 'ar'
                    ? 'يجب توفر موظف واحد على الأقل من مركز تطوير الأعمال ومستشار مبيعات واحد ومدير مبيعات واحد خلال جميع ساعات العمل. الكابتن وموظف التمويل مطلوبان خلال ساعات الذروة (10ص–2م، 4م–7م).'
                    : coverageModel?.minimumStaffing || 'At least 1 BDC Agent, 1 Sales Advisor, and 1 Sales Manager must be available during all business hours. Captain and Finance Officer required during peak hours (10AM-2PM, 4PM-7PM).'}
                </div>
              </div>
            </div>

            {/* Coverage Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">
                  {lang === 'ar' ? 'نموذج التغطية لكل فرع' : 'Per-Branch Coverage Model'}
                </h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="text-left px-4 py-3 font-medium text-gray-700">{lang === 'ar' ? 'الدور' : 'Role'}</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">{lang === 'ar' ? 'المجموعة' : 'Group'}</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">{lang === 'ar' ? 'الحد الأدنى' : 'Minimum'}</th>
                      <th className="text-center px-4 py-3 font-medium text-gray-700">{lang === 'ar' ? 'مُوصى به' : 'Recommended'}</th>
                      <th className="text-left px-4 py-3 font-medium text-gray-700">{lang === 'ar' ? 'ملاحظة' : 'Note'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {roles.map(role => {
                      const coverage = coverageModel?.rolesPerBranch?.find(r =>
                        r.roleId === role.id
                      );
                      const group = ORG_GROUPS.find(g => g.id === role.group);
                      return (
                        <tr key={role.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-800">
                            {lang === 'ar' ? role.titleAr : role.title}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${group?.lightColor || 'bg-gray-100'} ${group?.textColor || 'text-gray-700'}`}>
                              {group ? (lang === 'ar' ? group.labelAr : group.label) : role.group}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 bg-red-100 text-red-700 rounded-full font-bold text-sm">
                              {coverage?.minimumCount ?? 1}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 bg-green-100 text-green-700 rounded-full font-bold text-sm">
                              {coverage?.recommendedCount ?? 2}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs">
                            {role.id === 'captain' && (lang === 'ar' ? 'مطلوب في ساعات الذروة' : 'Required during peak hours')}
                            {role.id === 'finance_officer' && (lang === 'ar' ? 'مطلوب في ساعات الذروة' : 'Required during peak hours')}
                            {role.id === 'bdc_agent' && (lang === 'ar' ? 'يجب توفره في جميع الأوقات' : 'Must be available at all times')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Backup Rules */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <RefreshCw className="w-4 h-4 text-slate-500" />
                {lang === 'ar' ? 'قواعد التغطية الاحتياطية' : 'Backup Coverage Rules'}
              </h3>
              <div className="space-y-2">
                {(lang === 'ar'
                  ? coverageModel?.backupRulesAr || []
                  : coverageModel?.backupRules || []
                ).map((rule: string, i: number) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                    <span className="w-6 h-6 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-bold text-xs shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-sm text-gray-700">{rule}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Peak Hours */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-5">
              <h3 className="font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                {lang === 'ar' ? 'ساعات الذروة وتخصيص المهام' : 'Peak Hours & Role Allocation'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  {
                    period: lang === 'ar' ? '10:00 ص – 2:00 م' : '10:00 AM – 2:00 PM',
                    label: lang === 'ar' ? 'ذروة الصباح' : 'Morning Peak',
                    roles: lang === 'ar'
                      ? ['جميع وكلاء مركز تطوير الأعمال', 'الكابتن مطلوب', 'موظف التمويل مطلوب', 'مستشارو المبيعات نشطون']
                      : ['All BDC Agents active', 'Captain required on station', 'Finance Officer required', 'Sales Advisors active'],
                    color: 'border-indigo-300'
                  },
                  {
                    period: lang === 'ar' ? '4:00 م – 7:00 م' : '4:00 PM – 7:00 PM',
                    label: lang === 'ar' ? 'ذروة المساء' : 'Evening Peak',
                    roles: lang === 'ar'
                      ? ['موجة الاتصال المسائية من BDC', 'موجة المتابعة واتساب', 'مستشارو المبيعات للزيارات المسائية', 'الكابتن مطلوب']
                      : ['BDC evening contact wave', 'WhatsApp follow-up wave', 'Sales Advisors for evening visits', 'Captain required'],
                    color: 'border-purple-300'
                  }
                ].map((peak, i) => (
                  <div key={i} className={`bg-white border-2 rounded-xl p-4 ${peak.color}`}>
                    <div className="font-bold text-gray-900 text-lg">{peak.period}</div>
                    <div className="text-sm text-indigo-600 font-medium mb-3">{peak.label}</div>
                    <div className="space-y-1">
                      {peak.roles.map((r, ri) => (
                        <div key={ri} className="flex items-center gap-2 text-sm text-gray-700">
                          <CheckCircle className="w-3 h-3 text-indigo-500 shrink-0" />
                          {r}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OperatingModel;
